import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ORGANIZATION_CATEGORY_SHORT_LABELS,
  VERIFICATION_DOCUMENT_TYPE_LABELS,
  type OrganizationCategory,
} from '@safetyhub/shared';
import { Button, Card, Modal, useConfirmDialog } from '@safetyhub/ui';
import { BusinessStatusBadge } from './BusinessStatusBadge';
import { ModalCancelButton } from './ModalCancelButton';
import { InfoRow } from './InfoRow';
import {
  getVerificationDetail,
  approveVerification,
  rejectVerification,
  cancelRejection,
  updateVerificationFields,
  revalidateVerification,
  downloadVerificationDocument,
  REJECTION_REASON_LABELS,
  ACTIVE_REJECTION_REASONS,
  type VerificationDetail,
  type VerificationDocument,
  type AutoCheckResult,
  type FuzzyMatch,
  type VerificationRejectionReason,
  type BusinessStatus,
  type UpdateVerificationFieldsPayload,
} from '../lib/verification';

const CATEGORY_OPTIONS = [
  { value: 'WORKPLACE', label: '사업장' },
  { value: 'GUIDANCE_AGENCY', label: '지도기관' },
  { value: 'SAFETY_MANAGEMENT_AGENCY', label: '안전관리대행기관' },
];

function StatusBadge({ passed }: { passed: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {passed ? '통과' : '실패'}
    </span>
  );
}

function SectionCard({
  title,
  children,
  headerRight,
}: {
  title: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  return (
    <Card elevation="none" className="mb-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {headerRight}
      </div>
      {children}
    </Card>
  );
}

function AutoCheckSection({ result }: { result: AutoCheckResult | null }) {
  if (!result) {
    return <p className="text-sm text-gray-500">자동 검증 결과 없음</p>;
  }

  const topMatches: FuzzyMatch[] = Array.isArray(result.fuzzyMatch)
    ? (result.fuzzyMatch as FuzzyMatch[]).slice(0, 5)
    : [];

  const businessStatus = result.ntsValidation?.businessStatus as BusinessStatus | undefined;
  const isClosed = businessStatus === 'CLOSED';

  return (
    <dl>
      {businessStatus !== undefined && (
        <InfoRow
          label="사업자 상태"
          value={<BusinessStatusBadge value={businessStatus} />}
        />
      )}
      {isClosed && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-card">
          <p className="text-sm font-medium text-red-700">
            폐업된 사업자입니다 — 인증 불가
          </p>
        </div>
      )}
      <InfoRow
        label="체크섬 검증"
        value={<StatusBadge passed={Boolean(result.checksumValid)} />}
      />
      <InfoRow
        label="NTS 검증"
        value={
          <div className="flex flex-col gap-1">
            <StatusBadge passed={Boolean(result.ntsValidation?.valid)} />
            {result.ntsValidation?.reason && (
              <span className="text-xs text-gray-500">{String(result.ntsValidation.reason)}</span>
            )}
          </div>
        }
      />
      <div className="py-1.5">
        <dt className="text-sm font-medium text-gray-500 mb-2">
          퍼지 매칭 결과 (상위 {topMatches.length}건)
        </dt>
        {topMatches.length === 0 ? (
          <p className="text-sm text-gray-500">매칭 결과 없음</p>
        ) : (
          <table className="min-w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500">상호명</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">유사도</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {topMatches.map((m, i) => (
                <tr key={m.id ?? i} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{m.name}</td>
                  <td className="px-4 py-2">
                    {typeof m.similarity === 'number'
                      ? `${(m.similarity * 100).toFixed(1)}%`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </dl>
  );
}

/**
 * 심사 액션 sub 모달 셋(반려·승인·반려 취소)의 층 — 「기관 인증 심사」(z-[70]) 위에 겹쳐 뜨므로
 * 한 층 위(z-[80]) + 낮춘 배경(겹침 알파 보정, base `overlayBgClassName` 규약).
 *
 * 종전엔 부모 모달의 포탈 **안**에 `fixed … z-[60]`으로 그려져 부모 쌓임 맥락 안에서만 위였다. base
 * `Modal`은 body로 포탈되므로 층을 **여는 쪽이 명시**해야 한다(z 정책 70→80→90→100).
 */
const SUB_MODAL_LAYER = {
  overlayZClassName: 'z-[80]',
  overlayBgClassName: 'bg-black/25',
} as const;

function RejectModal({
  onConfirm,
  onCancel,
  loading,
}: {
  onConfirm: (reasonCode: VerificationRejectionReason, memo: string) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [reasonCode, setReasonCode] = useState<VerificationRejectionReason | ''>('');
  const [memo, setMemo] = useState('');

  const canSubmit = reasonCode !== '';

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm(reasonCode as VerificationRejectionReason, memo.trim());
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title="인증 반려"
      size="md"
      {...SUB_MODAL_LAYER}
      isDirty={reasonCode !== '' || memo !== ''}
      closeOnOverlay={!loading}
      closeOnEsc={!loading}
      showCloseButton={!loading}
      closeLocked={loading}
      footer={
        <>
          <ModalCancelButton onCancel={onCancel} disabled={loading} />
          <Button variant="danger" onClick={handleSubmit} disabled={loading || !canSubmit}>
            {loading ? '처리 중...' : '반려'}
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          반려 사유 <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value as VerificationRejectionReason | '')}
        >
          <option value="">사유를 선택하세요</option>
          {ACTIVE_REJECTION_REASONS.map((key) => (
            <option key={key} value={key}>
              {REJECTION_REASON_LABELS[key]}
            </option>
          ))}
        </select>
        {!reasonCode && <p className="text-xs text-red-500 mt-1">반려 사유를 선택해주세요.</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">추가 메모 (선택)</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          rows={3}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="추가 메모를 입력하세요 (선택사항)"
        />
      </div>
    </Modal>
  );
}

function ApproveModal({
  onConfirm,
  onCancel,
  loading,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <Modal
      open
      onClose={onCancel}
      title="인증 승인"
      size="md"
      {...SUB_MODAL_LAYER}
      closeOnOverlay={!loading}
      closeOnEsc={!loading}
      showCloseButton={!loading}
      closeLocked={loading}
      footer={
        <>
          <ModalCancelButton onCancel={onCancel} disabled={loading} />
          <Button variant="primary" onClick={onConfirm} disabled={loading}>
            {loading ? '처리 중...' : '승인'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600">이 조직의 인증을 승인하시겠습니까?</p>
    </Modal>
  );
}

function CancelRejectionModal({
  onConfirm,
  onCancel,
  loading,
}: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      open
      onClose={onCancel}
      title="반려 취소"
      size="md"
      {...SUB_MODAL_LAYER}
      isDirty={reason !== ''}
      closeOnOverlay={!loading}
      closeOnEsc={!loading}
      showCloseButton={!loading}
      closeLocked={loading}
      footer={
        <>
          <ModalCancelButton onCancel={onCancel} disabled={loading} />
          <Button variant="warning" onClick={() => onConfirm(reason.trim())} disabled={loading}>
            {loading ? '처리 중...' : '반려 취소'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">
        반려를 취소하면 이전 심사 상태로 복원됩니다. (기록된 상태가 없으면 수동 심사 상태로 전환)
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">취소 사유 (선택)</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="감사 로그에 기록될 취소 사유를 입력하세요 (선택사항)"
        />
      </div>
    </Modal>
  );
}

interface FieldEditState {
  category: string;
  registrationNumber: string;
  openingDate: string;
  ceoName: string;
  officialName: string;
  address: string;
}

function VerificationFieldsSection({
  org,
  onSaved,
}: {
  org: VerificationDetail;
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const initialState: FieldEditState = {
    category: org.category ?? '',
    registrationNumber: org.registrationNumber ?? '',
    openingDate: org.openingDate ?? '',
    ceoName: org.ceoName ?? '',
    officialName: org.officialName ?? '',
    address: org.address ?? '',
  };

  const [fields, setFields] = useState<FieldEditState>(initialState);
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setFields({
      category: org.category ?? '',
      registrationNumber: org.registrationNumber ?? '',
      openingDate: org.openingDate ?? '',
      ceoName: org.ceoName ?? '',
      officialName: org.officialName ?? '',
      address: org.address ?? '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: UpdateVerificationFieldsPayload = {};
      if (fields.category !== (org.category ?? '')) payload.category = fields.category;
      if (fields.registrationNumber !== (org.registrationNumber ?? ''))
        payload.registrationNumber = fields.registrationNumber;
      if (fields.openingDate !== (org.openingDate ?? ''))
        payload.openingDate = fields.openingDate;
      if (fields.ceoName !== (org.ceoName ?? ''))
        payload.ceoName = fields.ceoName;
      if (fields.officialName !== (org.officialName ?? ''))
        payload.officialName = fields.officialName;
      if (fields.address !== (org.address ?? ''))
        payload.address = fields.address;

      const res = await updateVerificationFields(org.id, payload);
      if (res.success) {
        setToast({ message: '저장 및 재검증 완료', type: 'success' });
        setIsEditing(false);
        onSaved();
      } else {
        setToast({ message: '저장 실패', type: 'error' });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      const msg = axiosErr?.response?.data?.error?.message ?? '저장 중 오류가 발생했습니다.';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const setField = (key: keyof FieldEditState, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <SectionCard
      title="신청자 입력 정보"
      headerRight={
        !isEditing ? (
          <Button variant="secondary" size="sm" onClick={handleEdit}>
            필드 보정
          </Button>
        ) : null
      }
    >
      {toast && (
        <div
          className={`mb-3 p-2 rounded text-sm ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {toast.message}
        </div>
      )}
      {!isEditing ? (
        <dl>
          <InfoRow label="카테고리" value={ORGANIZATION_CATEGORY_SHORT_LABELS[org.category as OrganizationCategory] ?? org.category} />
          <InfoRow label="사업자등록번호" value={org.registrationNumber} />
          <InfoRow label="개업일자" value={org.openingDate} />
          <InfoRow label="대표자명" value={org.ceoName} />
          <InfoRow label="상호" value={org.officialName} />
          <InfoRow label="주소" value={org.address} />
        </dl>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">카테고리</label>
            <select
              value={fields.category}
              onChange={(e) => setField('category', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">사업자등록번호</label>
            <input
              type="text"
              value={fields.registrationNumber}
              onChange={(e) => setField('registrationNumber', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">개업일자</label>
            <input
              type="text"
              value={fields.openingDate}
              onChange={(e) => setField('openingDate', e.target.value)}
              placeholder="YYYYMMDD"
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">대표자명</label>
            <input
              type="text"
              value={fields.ceoName}
              onChange={(e) => setField('ceoName', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">상호</label>
            <input
              type="text"
              value={fields.officialName}
              onChange={(e) => setField('officialName', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">주소</label>
            <input
              type="text"
              value={fields.address}
              onChange={(e) => setField('address', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '수정 후 재검증'}
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCancel} disabled={saving}>
              취소
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/**
 * 미리보기 판정의 **폴백** — 브라우저가 그릴 수 있는 형식만(`heic`·`heif`는 일부러 뺀다. 업로드는
 * 받지만 `<img>`가 못 그린다). 1차 축은 아래 {@link isImageBlob}의 `blob.type`이다.
 */
const PREVIEWABLE_FILENAME = /\.(jpg|jpeg|png|gif|webp)$/i;

/**
 * 이 서류를 **미리보기로 그릴 수 있나** — 1차 축은 `blob.type`이다.
 *
 * ## ⚠️ `doc.url`에서 파생하지 말 것 — 인증 라우트에는 **확장자가 없다**
 * 2026-10-19까지 이 판정이 `doc.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)`였는데, 서류 URL이 전용
 * 인증 라우트(`…/documents/:docId`)로 바뀌면서 경로 끝이 cuid가 됐다 — 그 형태로 두면 판정이
 * **언제나 거짓**이 되어 **모든 서류가 플레이스홀더**로 그려진다(심사자가 보려는 것이 바로
 * 이미지 미리보기다).
 *
 * ## 왜 `blob.type`이 1차이고 파일명이 폴백인가
 * `blob.type`은 **서버가 말한 값**이다(`Content-Type` — 라우트가 원장 `files.mimeType`을 싣는다).
 * 파일명 확장자는 **추측**이고, 확장자 없는 업로드 파일명이 가능하다. 그래서 순서가 이것이고,
 * `blob.type`이 빈 문자열일 때만(`Content-Type`이 없거나 브라우저가 비울 때) 파일명으로 떨어진다.
 *
 * ⚠️ **응답 계약을 넓혀 `mimeType`을 받는 길로 바꾸지 말 것** — 공용 매퍼
 * (`organizations/agency-verification-document.ts`)에 그 필드를 더하면 **web 조직 상세 응답까지
 * 함께 바뀐다**. 열람에 blob이 이미 필요하므로 그 blob이 답을 들고 있다.
 *
 * ⚠️ **tsc·spec이 원리적으로 못 보는 축이다** — 어느 축으로 갈라도 타입은 맞고(`url`·`fileName`
 * 둘 다 `string`이다) 틀린 결과는 **렌더**로만 드러나는데 jsdom에 레이아웃 엔진이 없고 이
 * 컴포넌트에는 렌더 spec도 없다. 그래서 소스 래칫
 * (`__tests__/verificationDocumentAuthAxis.test.ts`)이 이 축의 유일한 탐지기다.
 */
/**
 * 미리보기로 그릴 수 있는 **mime 목록** — 위 `PREVIEWABLE_FILENAME`과 **같은 집합**이어야 한다.
 *
 * ⚠️ **`blob.type.startsWith('image/')`로 넓히지 말 것.** `image/heic`·`image/heif`가 통과해
 * **그릴 수 없는 것을 그리려 한다**(업로드는 받지만 `<img>`가 못 그린다 — 아래 `fileKindLabel`
 * 주석이 「heic·png 실패」를 이미 적고 있다). 이 판정의 축은 **mime의 접두**가 아니라 **브라우저가
 * 그릴 수 있는 형식 목록**이다. 2026-10-19에 팀장이 접두 형태를 지시했다가 이 이유로 정정했고,
 * 그 축의 **렌더 탐지기**는 짝 spec의 heic 케이스다(소스 래칫은 「그 식이 코드에 있나」만 잰다).
 *
 * `image/jpg`는 비표준이지만 일부 업로드 경로가 그 값을 보낸다 — 빼면 그 서류만 조용히
 * 플레이스홀더가 된다.
 */
const PREVIEWABLE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

function isImageBlob(blob: Blob, fileName: string): boolean {
  // `; charset=…` 같은 파라미터가 붙어 올 수 있으므로 타입 본체만 본다.
  const mime = blob.type.split(';')[0].trim().toLowerCase();
  if (mime) return PREVIEWABLE_MIME_TYPES.includes(mime);
  return PREVIEWABLE_FILENAME.test(fileName);
}

/** 미리보기 불가 서류의 플레이스홀더 라벨 — 종전 하드코딩 「PDF」가 heic·png 실패에도 떴다. */
function fileKindLabel(fileName: string): string {
  const ext = /\.([a-z0-9]+)$/i.exec(fileName)?.[1];
  return ext ? ext.toUpperCase() : '파일';
}

/** object URL을 파일로 저장시킨다 — revoke는 **부르는 쪽이 소유한다**(URL의 수명이 서로 다르다). */
function triggerDownload(objectUrl: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * 검증 서류 한 건 — 미리보기와 내려받기 **둘 다 인증 요청**이다.
 *
 * ⚠️ `<img src={doc.url}>`·`<a href={doc.url}>`으로 되돌리지 말 것 — 그 라우트는 Bearer 헤더
 * 전용이라 평범한 내비게이션·이미지 로드가 **401**이다(사유는 `lib/verification.ts`의
 * `downloadVerificationDocument` JSDoc이 정본). 그래서 blob을 받아 object URL로 그리고,
 * **언마운트에서 revoke**한다(모달이라 닫힘이 곧 언마운트다 — 풀지 않으면 심사자가 상세를
 * 여닫는 동안 blob이 쌓인다).
 */
export function VerificationDocumentCard({
  companyId,
  doc,
}: {
  companyId: string;
  doc: VerificationDocument;
}) {
  /**
   * 받아 둔 서류 — **미리보기와 내려받기가 이것을 함께 쓴다.**
   *
   * ⚠️ 「미리보기 가능하면 그때 받는다」로 되돌리지 말 것 — 형식을 아는 축이 `blob.type`이므로
   * **blob이 먼저 와야 판정이 선다**(그 게이트는 파일명 추측으로만 성립한다). 그 대신 요청이
   * 문서당 **1회**로 줄었다(종전엔 미리보기 + 내려받기로 2회였다).
   */
  const [loaded, setLoaded] = useState<{ objectUrl: string; isImage: boolean } | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    downloadVerificationDocument(companyId, doc.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({ objectUrl, isImage: isImageBlob(blob, doc.fileName) });
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, doc.id, doc.fileName]);

  const handleDownload = async () => {
    // 이미 받아 둔 것이 있으면 **재요청하지 않는다** — 같은 object URL에 `download`만 붙인다.
    // ⚠️ 여기서 revoke하지 말 것 — 미리보기가 같은 URL을 쓰고 있고, 해제는 언마운트가 소유한다.
    if (loaded) {
      triggerDownload(loaded.objectUrl, doc.fileName);
      return;
    }
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await downloadVerificationDocument(companyId, doc.id);
      const objectUrl = window.URL.createObjectURL(blob);
      triggerDownload(objectUrl, doc.fileName);
      // 이 URL은 이 함수만 쓴다 — 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소하므로
      // 한 박자 뒤에 푼다.
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      // ⚠️ `responseType: 'blob'`이면 **에러 본문도 Blob**이라 서버 문구를 읽을 수 없다 — 일반 문구로.
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded p-3 flex flex-col gap-2">
      {/*
        종류 배지 — **파일명만으로는 무엇을 보고 있는지 알 수 없다**(`scan001.jpg`가 사업자등록증인지
        지정서인지 구별되지 않는다). 라벨 소유자는 shared `VERIFICATION_DOCUMENT_TYPE_LABELS`다 —
        여기서 한국어를 새로 적지 말 것(두 벌이 되면 한쪽이 낡는다).
        `type`이 없는 것은 **옛 번들 호환**으로 optional인 계약 때문이고 서버가 채우는 값은 항상 있다.
      */}
      {doc.type && (
        <span className="self-start inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          {VERIFICATION_DOCUMENT_TYPE_LABELS[doc.type]}
        </span>
      )}
      <div className="text-sm font-medium text-gray-700 truncate" title={doc.fileName}>
        {doc.fileName}
      </div>
      <div className="text-xs text-gray-500">
        {new Date(doc.uploadedAt).toLocaleDateString('ko-KR')}
      </div>
      {loadFailed ? (
        <div className="flex items-center justify-center h-20 bg-gray-50 rounded border text-gray-400">
          <span className="text-xs">미리보기 불가</span>
        </div>
      ) : !loaded ? (
        <div className="w-full h-40 rounded border bg-gray-100 animate-pulse" />
      ) : loaded.isImage ? (
        <img
          src={loaded.objectUrl}
          alt={doc.fileName}
          className="w-full h-40 object-cover rounded border"
          onError={() => setLoadFailed(true)}
        />
      ) : (
        <div className="flex items-center justify-center h-20 bg-gray-50 rounded border text-gray-400">
          <span className="text-xs">{fileKindLabel(doc.fileName)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        aria-busy={downloading}
        className="text-xs text-brand-600 hover:underline text-center cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
      >
        {downloading ? '내려받는 중…' : '다운로드'}
      </button>
      {downloadFailed && (
        <p className="text-xs text-red-600 text-center">
          내려받지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      )}
    </div>
  );
}

interface VerificationDetailModalProps {
  companyId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function VerificationDetailContent({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<'approve' | 'reject' | 'cancel-rejection' | null>(null);
  // NTS 재검증 확인 — 심사 sub 모달 셋과 같은 층(`SUB_MODAL_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();
  const [revalidateToast, setRevalidateToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [revalidating, setRevalidating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'verification', 'detail', companyId],
    queryFn: () => getVerificationDetail(companyId),
    enabled: Boolean(companyId),
  });

  const approveMutation = useMutation({
    mutationFn: () => approveVerification(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'verification'] });
      onClose();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({
      reasonCode,
      memo,
    }: {
      reasonCode: VerificationRejectionReason;
      memo?: string;
    }) => rejectVerification(companyId, reasonCode, memo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'verification'] });
      onClose();
    },
  });

  const cancelRejectionMutation = useMutation({
    mutationFn: ({ reason }: { reason?: string }) =>
      cancelRejection(companyId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'verification'] });
      onClose();
    },
  });

  const handleRejectConfirm = (reasonCode: VerificationRejectionReason, memo: string) => {
    rejectMutation.mutate({ reasonCode, memo: memo || undefined });
    setModal(null);
  };

  const handleCancelRejectionConfirm = (reason: string) => {
    cancelRejectionMutation.mutate({ reason: reason || undefined });
    setModal(null);
  };

  const handleApproveConfirm = () => {
    approveMutation.mutate();
    setModal(null);
  };

  const handleRevalidate = async () => {
    if (
      !(await confirm({
        title: 'NTS 재검증',
        body: 'NTS 재검증을 실행하시겠습니까?',
        confirmLabel: '재검증',
        ...SUB_MODAL_LAYER,
      }))
    )
      return;
    setRevalidating(true);
    try {
      const res = await revalidateVerification(companyId);
      if (res.success) {
        await queryClient.refetchQueries({
          queryKey: ['admin', 'verification', 'detail', companyId],
        });
        queryClient.invalidateQueries({ queryKey: ['admin', 'verification'] });
        setRevalidateToast({ message: '재검증 완료', type: 'success' });
      } else {
        setRevalidateToast({ message: '재검증 실패', type: 'error' });
      }
    } catch {
      setRevalidateToast({ message: '재검증 중 오류가 발생했습니다.', type: 'error' });
    } finally {
      setRevalidating(false);
      setTimeout(() => setRevalidateToast(null), 3000);
    }
  };

  const isMutating = approveMutation.isPending || rejectMutation.isPending || cancelRejectionMutation.isPending;
  const mutationError = approveMutation.error || rejectMutation.error || cancelRejectionMutation.error;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success || !data.data) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const org: VerificationDetail = data.data;
  const documents = Array.isArray(org.verificationDocuments)
    ? org.verificationDocuments
    : [];

  const businessStatus = org.agencyAutoCheckResult?.ntsValidation?.businessStatus ?? org.ntsValidation?.businessStatus;
  const isClosed = businessStatus === 'CLOSED';
  const APPROVABLE_STATUSES = ['PENDING', 'REVIEW_TIMEOUT', 'REVIEW_NO_MATCH', 'REVIEW_AMBIGUOUS', 'INFO_MISMATCH', 'REVIEW_REAPPLIED'];
  const isPending = APPROVABLE_STATUSES.includes(org.verificationStatus);

  return (
    <div>
      {org.isManualReview && (
        <div className="mb-4">
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            수동 심사 필요
          </span>
        </div>
      )}

      {mutationError && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded text-sm">
          처리 중 오류가 발생했습니다.
        </div>
      )}

      {revalidateToast && (
        <div
          className={`mb-4 p-3 rounded text-sm ${
            revalidateToast.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {revalidateToast.message}
        </div>
      )}

      {/* 섹션 1: 조직 기본 정보 */}
      <SectionCard title="조직 기본 정보">
        <dl>
          <InfoRow label="조직명" value={org.name} />
          <InfoRow
            label="카테고리"
            value={ORGANIZATION_CATEGORY_SHORT_LABELS[org.category as OrganizationCategory] ?? org.category}
          />
          <InfoRow
            label="등록일"
            value={new Date(org.createdAt).toLocaleDateString('ko-KR')}
          />
          {org.ownerUser && (
            <>
              <InfoRow label="Owner" value={org.ownerUser.name} />
              <InfoRow label="연락처" value={org.ownerUser.phone ?? '-'} />
            </>
          )}
        </dl>
      </SectionCard>

      {/* 섹션 2: 신청자 입력 정보 (편집 가능) */}
      <VerificationFieldsSection
        org={org}
        onSaved={async () => {
          await queryClient.refetchQueries({
            queryKey: ['admin', 'verification', 'detail', companyId],
          });
          queryClient.invalidateQueries({ queryKey: ['admin', 'verification'] });
        }}
      />

      {/* 섹션 3: 자동 검증 결과 */}
      <SectionCard title="자동 검증 결과">
        <AutoCheckSection result={org.agencyAutoCheckResult} />
      </SectionCard>

      {/*
        섹션 4: 인증 서류
        ⚠️ 제목이 **그 집합을 말해야 한다** — 종전 「지정서 목록 (N건)」은 사업자등록증까지 세고
        있어서 N이 맞는 채 **라벨만 거짓**이었다(그 종류의 결함은 tsc·spec이 원리적으로 못 잡는다).
        사업자등록증을 **빼지 않는** 것이 요점이다 — 둘 다 심사 재료이고, 처방은 필터가 아니라
        「제목을 집합에 맞추고 항목마다 종류를 보이게」다.
      */}
      <SectionCard title={`인증 서류 (${documents.length}건)`}>
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500">업로드된 서류가 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {documents.map((doc) => (
              <VerificationDocumentCard key={doc.id} companyId={companyId} doc={doc} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* 섹션 5: 액션 */}
      {isPending && (
        <SectionCard title="심사 액션">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="relative group">
              <Button
                variant="primary"
                className="px-6"
                onClick={() => setModal('approve')}
                disabled={isMutating || isClosed}
              >
                승인
              </Button>
              {isClosed && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  폐업된 사업자는 승인할 수 없습니다
                </div>
              )}
            </div>
            <Button
              variant="danger"
              className="px-6"
              onClick={() => setModal('reject')}
              disabled={isMutating}
            >
              반려
            </Button>
            <Button
              variant="secondary"
              className="px-6"
              onClick={handleRevalidate}
              disabled={isMutating || revalidating}
            >
              {revalidating ? '재검증 중...' : '수동 재검증'}
            </Button>
          </div>
        </SectionCard>
      )}

      {!isPending && (
        <div
          className={`p-4 rounded mb-4 ${
            org.verificationStatus === 'VERIFIED'
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
        >
          {org.verificationStatus === 'VERIFIED' ? (
            '이미 승인된 조직입니다.'
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span>
                {`반려된 조직입니다. 사유: ${
                  org.rejectionReasonCode
                    ? REJECTION_REASON_LABELS[org.rejectionReasonCode]
                    : (org.rejectionReason ?? '-')
                }`}
              </span>
              <Button
                variant="warning"
                size="sm"
                className="whitespace-nowrap"
                onClick={() => setModal('cancel-rejection')}
                disabled={isMutating}
              >
                반려 취소
              </Button>
            </div>
          )}
        </div>
      )}
      {isPending && org.verificationStatus !== 'PENDING' && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
          {org.verificationStatus === 'REVIEW_TIMEOUT' && '자동 검증 시간 초과 — 서류 검토 후 수동 심사 진행'}
          {org.verificationStatus === 'REVIEW_NO_MATCH' && 'NTS 통과, 공시 미매칭 — 지정서 확인 후 수동 심사 진행'}
          {org.verificationStatus === 'REVIEW_AMBIGUOUS' && '공시 후보 다수 — 아래 유사 기관 후보 중 정확한 기관을 확인 후 승인/반려'}
          {org.verificationStatus === 'INFO_MISMATCH' && '사업자 정보 불일치 — 신청자 정정 대기 중'}
          {org.verificationStatus === 'REVIEW_REAPPLIED' && '반려 후 재신청 — 재업로드된 서류 검토 후 수동 심사 진행'}
        </div>
      )}

      {modal === 'reject' && (
        <RejectModal
          onConfirm={handleRejectConfirm}
          onCancel={() => setModal(null)}
          loading={isMutating}
        />
      )}

      {modal === 'approve' && (
        <ApproveModal
          onConfirm={handleApproveConfirm}
          onCancel={() => setModal(null)}
          loading={isMutating}
        />
      )}

      {modal === 'cancel-rejection' && (
        <CancelRejectionModal
          onConfirm={handleCancelRejectionConfirm}
          onCancel={() => setModal(null)}
          loading={isMutating}
        />
      )}
      {confirmDialog}
    </div>
  );
}

export default function VerificationDetailModal({
  companyId,
  isOpen,
  onClose,
}: VerificationDetailModalProps) {
  return (
    // 본문 스크롤은 base의 `bodyScroll`(기본)이 맡는다 — 헤더(제목·✕)가 고정되고 본문만 스크롤된다.
    // 종전엔 본문에 손으로 `max-h-[75vh] overflow-y-auto`를 걸어 같은 일을 했다(같은 property가 두 겹이면
    // 스크롤러가 둘이 되므로 걷었다).
    <Modal open={isOpen} onClose={onClose} title="기관 인증 심사" size="xl">
      {companyId && (
        <VerificationDetailContent
          companyId={companyId}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}
